import { ticks as d3Ticks } from 'd3-array';
import React from 'react';

import { useIsMobile } from 'hooks/useIsMobile';
import { abbreviateNumber, shortPriceFormat } from 'lib/display/formatting';
import { useSelectMarketTickSize } from 'reduxState/markets/listed/listedMarketsUtils';

import { depthChartStyle } from './chartConfig';
import { ScaleFn } from './types';

export default function MarketDepthAxes({
  xMin,
  xMax,
  yMin,
  yMax,
  marketPair,
  xScale,
  yScale,
}: {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  marketPair: string;
  xScale: ScaleFn;
  yScale: ScaleFn;
}) {
  const isMobile = useIsMobile();
  const currentMarketTickSize = useSelectMarketTickSize(marketPair);
  const xTickValues = d3Ticks(xMin, xMax, 7);
  const yTickValues = d3Ticks(yMin, yMax, 4);

  const style = {
    tickLabel: {
      fontSize: 12,
      fontWeight: 'normal',
      fill: 'var(--gray4)',
    },
    grid: {
      strokeOpacity: 0.2,
      strokeWidth: 1,
      stroke: 'gray',
    },
  };

  return (
    <>
      {/* Horizontal grid (x) */}
      <g>
        {xTickValues.map((value, index) => {
          // Reduce number of texts so it will prevent overfloating on mobile
          if (isMobile && index % 2 !== 0) {
            return null;
          }
          const x = Math.round(xScale(value));
          const y1 = yScale(yMin) + 25;
          return (
            <g key={index}>
              <line x1={x} y1={y1} x2={x} y2={yScale(yMax)} {...style.grid} />
              <text x={x} y={y1} dx="0.2em" dy="-0.5em" {...style.tickLabel}>
                {currentMarketTickSize &&
                  shortPriceFormat(
                    value,
                    currentMarketTickSize.visibleDecimals,
                    isMobile ? 0 : 1
                  )}
              </text>
            </g>
          );
        })}
      </g>
      {/* Vertical grid (y) */}
      <g>
        {yTickValues.map((value, index) => {
          const y = Math.round(yScale(value));
          return (
            <g key={index}>
              <line
                x1={xScale(xMin)}
                y1={y}
                x2={xScale(xMax)}
                y2={y}
                {...style.grid}
              />
              <text
                x={xScale(xMin) - depthChartStyle.margin.leftHalf + 5}
                y={y}
                dy="-0.3em"
                dx="0.1em"
                {...style.tickLabel}
              >
                {abbreviateNumber(value)}
              </text>
            </g>
          );
        })}
      </g>
    </>
  );
}
